import { RowActions } from "@/shared/ui/RowActions";
import { Badge, DataTable, type TanstackReactTable } from "@devhop/ui";
import { EyeIcon } from "lucide-react";
import type { ScheduleChangeLogListItemDTO } from "../../domain/contracts/schedule-logs";

const sessionTypeLabels: Record<string, string> = {
  class_session: "ຄລາດສ໌",
  private_lesson_booking: "ວິຊາສ່ວນຕົວ",
  room_booking: "ຈອງຫ້ອງ",
};

const changeTypeLabels: Record<string, string> = {
  time: "ປັບເວລາ",
  room: "ປ່ຽນຫ້ອງ",
  instructor: "ປ່ຽນຄູ",
  capacity: "ປ່ຽນຄວາມຈຸ",
  other: "ອື່ນໆ",
};

type ScheduleChangeLogTableProps = {
  isLoading: boolean;
  data: ScheduleChangeLogListItemDTO[];
  offset: number;
  limit: number;
  totalCount: number;
  onPaginationChange: (offset: number, limit: number) => void;
  onView: (id: string) => void;
};

export function ScheduleChangeLogTable({
  isLoading,
  data,
  offset,
  limit,
  totalCount,
  onPaginationChange,
  onView,
}: ScheduleChangeLogTableProps) {
  const columns: TanstackReactTable.ColumnDef<ScheduleChangeLogListItemDTO>[] =
    [
      {
        id: "changedAt",
        header: "ວັນທີ/ເວລາ",
        size: 140,
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-muted-foreground text-xs">
            {row.original.changedAt
              ? new Date(row.original.changedAt).toLocaleString()
              : "-"}
          </span>
        ),
      },
      {
        id: "sessionType",
        header: "ປະເພດ",
        size: 100,
        cell: ({ row }) => (
          <Badge variant="secondary" className="text-[11px]">
            {sessionTypeLabels[row.original.sessionType] ??
              row.original.sessionType}
          </Badge>
        ),
      },
      {
        id: "sessionInfo",
        header: "ຂໍ້ມູນຊົ່ວໂມງຮຽນ",
        size: 180,
        cell: ({ row }) => (
          <span
            title={row.original.sessionInfo}
            className="block max-w-[200px] truncate text-xs"
          >
            {row.original.sessionInfo || "-"}
          </span>
        ),
      },
      {
        id: "changeType",
        header: "ປະເພດການປ່ຽນແປງ",
        size: 100,
        cell: ({ row }) => (
          <span className="text-xs">
            {changeTypeLabels[row.original.changeType] ??
              row.original.changeType}
          </span>
        ),
      },
      {
        id: "oldValue",
        header: "ຄ່າເກົ່າ",
        size: 120,
        cell: ({ row }) => (
          <span
            title={row.original.oldValue ?? ""}
            className="block max-w-[120px] truncate font-mono text-xs"
          >
            {row.original.oldValue ?? "-"}
          </span>
        ),
      },
      {
        id: "newValue",
        header: "ຄ່າໃໝ່",
        size: 120,
        cell: ({ row }) => (
          <span
            title={row.original.newValue ?? ""}
            className="block max-w-[120px] truncate font-mono text-xs"
          >
            {row.original.newValue ?? "-"}
          </span>
        ),
      },
      {
        id: "reason",
        header: "ເຫດຜົນ",
        size: 140,
        cell: ({ row }) => (
          <span
            title={row.original.reason}
            className="block max-w-[160px] truncate text-xs"
          >
            {row.original.reason || "-"}
          </span>
        ),
      },
      {
        id: "changedBy",
        header: "ຜູ້ປ່ຽນແປງ",
        size: 120,
        cell: ({ row }) => (
          <span className="text-xs">
            {row.original.changedByUserName ?? row.original.changedBy}
          </span>
        ),
      },
      {
        id: "actions",
        cell: ({ row }) => (
          <RowActions
            actions={[
              {
                label: "ເບິ່ງລາຍລະອຽດ",
                icon: <EyeIcon className="size-4" />,
                onClick: () => onView(row.original.id),
              },
            ]}
          />
        ),
      },
    ];

  return (
    <DataTable<ScheduleChangeLogListItemDTO, unknown>
      noDataMessage="ບໍ່ພົບບັນທຶກການປ່ຽນແປງຕາຕະລາງ"
      isLoading={isLoading}
      columns={columns}
      data={data}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
    />
  );
}
